import { useMemo, useState } from "react";
import { Link } from "react-router";
import { renderBatchLabel } from "@/lib/formatters";
import "./BatchSlaTable.css";

/**
 * Phase 18 WU-18.E5 — per-batch SLA table.
 *
 * Rows come from `/api/analytics/batch-sla`. Status is derived client-side:
 * - "missed"      — slaMet === false
 * - "met"         — slaMet === true
 * - "approaching" — slaMet == null AND totalDurationMs / slaTargetMs >= 0.9
 * - "unknown"     — everything else (no SLA target)
 *
 * Filter chips at the top narrow visible rows. Clicking a row calls
 * `onBatchClick` (the AnalyticsPage SLA tab wires this to navigate
 * to `/analytics/batches/:id/trail`, the WU-18.F4 drill-through).
 * The batch ID cell also wraps a `<Link>` pointing at the same trail
 * destination for keyboard + screen-reader access; `stopPropagation`
 * keeps the row handler from firing on link click.
 */
export interface BatchSlaRow {
  batchId: string;
  /** R-132 — structured batch identifier, null for legacy / cold-tier rows. */
  batchDisplayId?: string | null;
  /** R-167 WU-7 — operator-rendered frozen label joined from Batch. */
  batchCustomDisplayLabel?: string | null;
  jobTypeId: string;
  completedAt: string; // ISO
  totalDurationMs: number;
  docCount: number;
  slaMet: boolean | null;
  slaTargetMs: number | null;
}

export interface BatchSlaTableProps {
  rows: BatchSlaRow[];
  onBatchClick?: (batchId: string) => void;
}

type Status = "met" | "missed" | "approaching" | "unknown";
type Filter = "all" | "met" | "missed" | "approaching";

const FILTERS: Filter[] = ["all", "met", "approaching", "missed"];

function statusOf(r: BatchSlaRow): Status {
  if (r.slaMet === true) return "met";
  if (r.slaMet === false) return "missed";
  if (r.slaTargetMs != null && r.slaTargetMs > 0) {
    const ratio = r.totalDurationMs / r.slaTargetMs;
    if (ratio >= 0.9) return "approaching";
  }
  return "unknown";
}

function statusClass(s: Status): string {
  switch (s) {
    case "met":
      return "batch-sla__chip batch-sla__chip--met";
    case "missed":
      return "batch-sla__chip batch-sla__chip--missed";
    case "approaching":
      return "batch-sla__chip batch-sla__chip--approaching";
    case "unknown":
      return "batch-sla__chip batch-sla__chip--unknown";
  }
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(2)}h`;
}

function formatDate(iso: string): string {
  return iso.slice(0, 16).replace("T", " ");
}

// Urgency for sorting: missed > approaching > unknown > met (most alarming first).
const URGENCY: Record<Status, number> = {
  missed: 3,
  approaching: 2,
  unknown: 1,
  met: 0,
};

export function BatchSlaTable({ rows, onBatchClick }: BatchSlaTableProps) {
  const [filter, setFilter] = useState<Filter>("all");

  const visible = useMemo(() => {
    const withStatus = rows.map((r) => ({ r, s: statusOf(r) }));
    const filtered = filter === "all" ? withStatus : withStatus.filter((x) => x.s === filter);
    filtered.sort((a, b) => URGENCY[b.s] - URGENCY[a.s]);
    return filtered;
  }, [rows, filter]);

  if (rows.length === 0) {
    return (
      <div className="batch-sla__empty" data-testid="batch-sla-empty">
        No batches completed in this period.
      </div>
    );
  }

  return (
    <div className="batch-sla">
      <div className="batch-sla__filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            className={`batch-sla__filter-btn${filter === f ? " batch-sla__filter-btn--active" : ""}`}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
          >
            {f}
          </button>
        ))}
      </div>
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Batch</th>
            <th>Job Type</th>
            <th>Completed</th>
            <th>Docs</th>
            <th>Duration</th>
            <th>Target</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(({ r, s }) => {
            const statusClassName = statusClass(s);
            return (
              <tr
                key={r.batchId}
                className="batch-sla__row"
                data-testid={`batch-sla-row-${r.batchId}`}
                role="button"
                tabIndex={0}
                onClick={() => onBatchClick?.(r.batchId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onBatchClick?.(r.batchId);
                  }
                }}
              >
                <td>
                  {/* R-167 WU-7 — render preferredDisplayLabel(batch) so the
                      SLA table matches BatchListPage's identifier. Cold-tier
                      synthetic rows (no real Batch) carry undefined display
                      fields — renderBatchLabel falls back to `B-<cuid>`. */}
                  <Link
                    to={`/analytics/batches/${r.batchId}/trail`}
                    className="analytics-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderBatchLabel({
                      id: r.batchId,
                      displayId: r.batchDisplayId ?? null,
                      customDisplayLabel: r.batchCustomDisplayLabel ?? null,
                    })}
                  </Link>
                </td>
                <td>{r.jobTypeId}</td>
                <td>{formatDate(r.completedAt)}</td>
                <td>{r.docCount.toLocaleString()}</td>
                <td>{formatDuration(r.totalDurationMs)}</td>
                <td>
                  {r.slaTargetMs != null && r.slaTargetMs > 0
                    ? formatDuration(r.slaTargetMs)
                    : "—"}
                </td>
                <td>
                  <span className={statusClassName}>{s}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
